import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Story", href: "#about" },
  { label: "Shop", href: "#shop" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

export default function Header({ onOrder }) {
  const { count, setIsOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-stone-50/85 backdrop-blur-xl border-b border-stone-200/80"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
        <a href="#home" className="flex items-center gap-2 group" data-testid="nav-logo">
          <span className="w-10 h-10 rounded-full bg-[#993311] text-stone-50 grid place-items-center font-serif text-lg">
            H
          </span>
          <div className="leading-tight">
            <div className="font-serif text-xl md:text-2xl text-stone-900 font-semibold">
              Halli Chutney
            </div>
            <div className="font-hand text-[#993311] text-sm -mt-1">
              taste of the village
            </div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-stone-700 hover:text-[#993311] transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-[#993311] hover:after:w-full after:transition-all"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsOpen(true)}
            className="relative p-2.5 rounded-full hover:bg-stone-200/70 transition-colors"
            data-testid="nav-cart-toggle"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5 text-stone-800" />
            {count > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 bg-[#993311] text-stone-50 text-[11px] font-bold rounded-full grid place-items-center"
                data-testid="cart-count-badge"
              >
                {count}
              </span>
            )}
          </button>
          <Button
            onClick={onOrder}
            className="hidden sm:inline-flex bg-[#993311] hover:bg-[#7A290E] text-stone-50 rounded-full px-5 h-10 transition-transform hover:-translate-y-0.5"
            data-testid="header-order-btn"
          >
            Order Now
          </Button>
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2 rounded-full hover:bg-stone-200/70"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-stone-50 border-t border-stone-200"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-base font-medium text-stone-800 hover:text-[#993311]"
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
              <Button
                onClick={() => {
                  setMobileOpen(false);
                  onOrder();
                }}
                className="bg-[#993311] hover:bg-[#7A290E] text-stone-50 rounded-full w-full"
                data-testid="mobile-order-btn"
              >
                Order Now
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
